import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import Skeleton from '@mui/material/Skeleton';
import DateRangePicker from './components/ui/Calendar/DateRangePicker';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { motion } from 'framer-motion';
import './CalendarView.css';
import Swal from 'sweetalert2';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
const guestOptionKeys = []; // Since guest pricing is removed, keep this as an empty array
const apiURL = process.env.REACT_APP_API_URL || 'http://localhost:7071';

const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();


// propertyId state is now reactive, see below in component


const CalendarView = () => {
  const calendarContainerRef = useRef(null);
  const [visibleRange, setVisibleRange] = useState({ startIdx: 0, endIdx: 30 });
  const [propertyId, setPropertyId] = useState(null);
  const [rates, setRates] = useState([]);
  const [loadingRates, setLoadingRates] = useState(true);
  const [editing, setEditing] = useState({});
  const [globalRoomOnlyRate, setGlobalRoomOnlyRate] = useState({});
  const [togglingAvailability, setTogglingAvailability] = useState({});
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());

  // Remove visibleMonths logic and infinite scroll, use visibleDays based on month/year only
  const [visibleDays, setVisibleDays] = useState([]);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const next30 = new Date();
    next30.setDate(today.getDate() + 30);
    return [{
      startDate: today,
      endDate: next30,
      key: 'selection'
    }];
  });

  // Utility: getDaysInMonth (unused with dateRange)
  function getDaysInMonth(month, year) {
    const days = [];
    const totalDays = new Date(year, month + 1, 0).getDate();
    for (let day = 1; day <= totalDays; day++) {
      days.push({
        day,
        month,
        year,
        dateStr: new Date(year, month, day).toLocaleDateString('sv-SE'),
      });
    }
    return days;
  }

  // useEffect(() => {
  //   const savedRange = localStorage.getItem('calendarDateRange');
  //   if (savedRange) {
  //     try {
  //       const parsed = JSON.parse(savedRange);
  //       setDateRange([{
  //         startDate: new Date(parsed.startDate),
  //         endDate: new Date(parsed.endDate),
  //         key: 'selection'
  //       }]);
  //     } catch (e) {
  //       console.warn('Invalid date range in localStorage');
  //     }
  //   }
  // }, []);

  useEffect(() => {
    const { startDate, endDate } = dateRange[0];
    const days = [];
    let current = new Date(startDate);

    while (current <= endDate) {
      days.push({
        day: current.getDate(),
        month: current.getMonth(),
        year: current.getFullYear(),
        dateStr: current.toLocaleDateString('sv-SE'),
      });
      current.setDate(current.getDate() + 1);
    }

    setVisibleDays(days);
    if (mainScrollRef.current) {
      mainScrollRef.current.scrollLeft = 0;
    }

    // Save to localStorage
    localStorage.setItem('calendarDateRange', JSON.stringify({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    }));
  }, [dateRange]);
  const [zoom, setZoom] = useState(1); 
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [selectedCells, setSelectedCells] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [selectionContext, setSelectionContext] = useState(null);
  const [promotionRate, setPromotionRate] = useState([]); // <-- add this
  const [showPromoRates, setShowPromoRates] = useState(false);
  const [minRate, setMinRate] = useState(0);
  const [maxRate, setMaxRate] = useState(9999);
  const [netBookedSummary, setNetBookedSummary] = useState({});
  const mainScrollRef = useRef();
  const mirrorScrollRef = useRef();
  // Remove overbookedDates state, we'll compute it via useMemo
  // Compute groupedByRoom above overbookedDates to avoid temporal dead zone
  const groupedByRoom = useMemo(() => {
    const map = {};
    rates.forEach(rate => {
      const dateStr = rate.date;
      const roomType = rate.roomType?.trim();
      if (!roomType || !dateStr) return;
      if (!map[roomType]) map[roomType] = {};
      map[roomType][dateStr] = { ...rate, key: `${roomType}_${dateStr}` };
    });
    return map;
  }, [rates]);
  // Compute overbookedRooms via useMemo
  const overbookedRooms = useMemo(() => {
    const overbookedSet = new Set();
    Object.entries(groupedByRoom).forEach(([roomType, dailyRates]) => {
      const normalizedRoomType = roomType.trim().toLowerCase();
      visibleDays.forEach(({ day, month, year, dateStr }) => {
        const booked = netBookedSummary[normalizedRoomType]?.[dateStr] ?? 0;
        const roomsToSell = dailyRates[dateStr]?.roomsToSell ?? 0;
        if (booked > roomsToSell) {
          overbookedSet.add(`${roomType}_${dateStr}`);
        }
      });
    });
    return overbookedSet;
  }, [groupedByRoom, netBookedSummary, visibleDays]);

  useEffect(() => {
    if (!propertyId) return;
    fetch(`${apiURL}/api/bookings/net-booked-summary?propertyId=${propertyId}&month=${month}&year=${year}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          console.log("📦 Received netBookedSummary from backend:", data.summary);
          const normalizedSummary = {};
          Object.entries(data.summary).forEach(([roomType, dateMap]) => {
            const key = roomType.trim().toLowerCase();
            normalizedSummary[key] = dateMap;
          });
          setNetBookedSummary(normalizedSummary);
        } else {
          console.warn("⚠️ Failed to load netBookedSummary:", data);
        }
      })
      .catch(err => {
        console.error('Failed to fetch net booked summary:', err);
      });
  }, [propertyId, month, year]);

  // Set propertyId from localStorage on mount
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    const pid = storedUser?.propertyId?.id || storedUser?.properties?.[0]?.propertyId?.id;
    setPropertyId(pid);
    console.log("🔁 Loaded propertyId in CalendarView:", pid);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const isClickInsideCalendar = calendarContainerRef.current && calendarContainerRef.current.contains(event.target);
      const isClickInsideSwal = event.target.closest('.swal2-container');

      if (!isClickInsideCalendar && !isClickInsideSwal) {
        setSelectedCells([]);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        setSelectionContext(null);
      }
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging]);

  const handleZoomChange = (e) => {
    setZoom(parseFloat(e.target.value));
  };


  // Helper function to fetch promotions for this property
  const fetchPromotionRates = useCallback(async () => {
    if (!propertyId) return;
    try {
      const res = await fetch(`${apiURL}/api/promotions?propertyId=${propertyId}`);
      const data = await res.json();
      setPromotionRate(data);
    } catch (err) {
      console.error("Failed to reload promotions:", err);
    }
  }, [propertyId, setPromotionRate]);

  useEffect(() => {
    if (!propertyId) return;
    fetchPromotionRates();
  }, [propertyId, fetchPromotionRates]);

  useEffect(() => {
    if (!propertyId) return;
    fetch(`${apiURL}/api/rate-limits?propertyId=${propertyId}`)
      .then(res => res.json())
      .then(data => {
        setMinRate(data.minRate);
        setMaxRate(data.maxRate);
      });
  }, [propertyId]);

  useEffect(() => {
    if (!propertyId) return;
    const timeout = setTimeout(() => {
      fetch(`${apiURL}/api/rate-limits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ minRate, maxRate, propertyId })
      })
        .then(res => {
          if (!res.ok) throw new Error("Failed to update rate limits");
          return res.json();
        })
        .then(data => {
          // Rate limits updated
        })
        .catch(err => {
          console.error('❌ Failed to update rate limits:', err);
        });
    }, 500);
  
    return () => clearTimeout(timeout);
  }, [minRate, maxRate, propertyId]);

  useEffect(() => {
    if (!propertyId) return;
    setLoadingRates(true);
    fetch(`${apiURL}/api/rates?propertyId=${propertyId}`)
      .then(res => res.json())
      .then(data => {
        setRates(data);

        const latestEdit = {};
        const grouped = {};

        data.forEach(rate => {
          if (!rate.roomType) return;
          const room = rate.roomType.trim();
          const guests = rate.breakfastGuests;
          if (!grouped[room]) grouped[room] = guests;
        });

        Object.entries(grouped).forEach(([roomType, breakfastGuests]) => {
          latestEdit[`__${roomType}_globalBreakfast`] = breakfastGuests || 0;

          const firstRate = data.find(r => r.roomType?.trim() === roomType);
        });

        setEditing(prev => ({ ...prev, ...latestEdit }));
      })
      .catch(err => console.error("Failed to load rates:", err))
      .finally(() => setLoadingRates(false));
  }, [month, year, propertyId]);


  const handleEdit = (roomType, dateStr, field, value) => {
    const key = `${roomType}_${dateStr}`;
    setEditing(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
    // setTimeout(() => handleBlur(roomType, dateStr), 0);
  };


const handleBlur = async (roomType, dateStr, overrideFields = {}) => {
  roomType = roomType.trim();
  const key = `${roomType}_${dateStr}`;
  const edited = { ...editing[key], ...overrideFields };

    const rate = groupedByRoom[roomType]?.[dateStr] || {};
    const availability = edited.hasOwnProperty('availability')
      ? edited.availability
      : rate.hasOwnProperty('availability') ? rate.availability : true;


    const guestPricing = {};
    const price = (edited.price !== undefined && !isNaN(edited.price))
      ? Number(edited.price)
      : (rate.price !== undefined ? Number(rate.price) : 0);
    const roomsToSell = edited.hasOwnProperty('roomsToSell')
      ? parseInt(edited.roomsToSell)
      : rate.roomsToSell;
    const breakfastGuests = parseInt(edited.breakfastGuests ?? rate.breakfastGuests ?? 0);
    const hasPriceChanged = edited.hasOwnProperty('price');
    const hasGuestPricing = Object.keys(guestPricing).length > 0;
    const hasAvailabilityChange = edited.hasOwnProperty('availability');
    const hasMeaningfulUpdate = (
      hasPriceChanged ||
      hasGuestPricing ||
      roomsToSell !== 0 ||
      breakfastGuests !== 0 ||
      hasAvailabilityChange
    );

    // if (!hasMeaningfulUpdate) {
    //   console.warn('Nothing to update...');
    //   return;
    // }
    const ratePlan = edited.ratePlan || rate.ratePlan || '';
  const roomOnlyRate = (edited.roomOnlyRate !== undefined && !isNaN(edited.roomOnlyRate))
      ? Number(edited.roomOnlyRate)
      : (rate.roomOnlyRate !== undefined ? Number(rate.roomOnlyRate) : 0);
  if (price < minRate) {
    toast.warn('Rate with breakfast is below minimum');
  } else if (price > maxRate) {
    toast.warn('Rate with breakfast is above maximum');
  }

  if (roomOnlyRate < minRate) {
    toast.warn('Room-only rate is below minimum');
  } else if (roomOnlyRate > maxRate) {
    toast.warn('Room-only rate is above maximum');
  }
    const payload = {
      roomType,
      date: dateStr,
      availability,
      roomOnlyRate,
      breakfastGuests,
      price,
      roomsToSell,
      propertyId // ✅ add this
    };

    try {
      const res = await fetch(`${apiURL}/api/rates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        console.error('❌ Rate update failed:', await res.text());
        return;
      }

      const result = await res.json();

      // 🧼 Clear stale override so the UI reflects updated backend state
      setEditing(prev => {
        const { [key]: removed, ...rest } = prev;
        return rest;
      });

      // Immediately update only the specific rate in the UI
      setRates(prevRates =>
        prevRates.map(r => (r._id === result._id ? result : r))
      );
    } catch (err) {
      console.error("Failed to update rate:", err);
    }
  };

  const reloadRatesAfterUpdate = () => {
    if (!propertyId) return;
    fetch(`${apiURL}/api/rates?propertyId=${propertyId}`)
      .then(res => res.json())
      .then(data => {
        setRates(data);
        // Do not overwrite editing here to avoid flicker
        fetchPromotionRates(); // 🔁 Also re-fetch promotions
      })
      .catch(err => console.error('Failed to reload rates:', err));
  };

  useEffect(() => {
    const main = mainScrollRef.current;
    const mirror = mirrorScrollRef.current;
    if (!main || !mirror) return;

    const syncScroll = () => mirror.scrollLeft = main.scrollLeft;
    const syncMirror = () => main.scrollLeft = mirror.scrollLeft;

    main.addEventListener('scroll', syncScroll);
    mirror.addEventListener('scroll', syncMirror);

    return () => {
      main.removeEventListener('scroll', syncScroll);
      mirror.removeEventListener('scroll', syncMirror);
    };
  }, []);

  // Track visible scroll range for header
  useEffect(() => {
    const main = mainScrollRef.current;
    if (!main) return;

    const updateRange = () => {
      const scrollLeft = main.scrollLeft;
      const cellWidth = 75;
      const viewWidth = main.clientWidth;

      const startIdx = Math.floor(scrollLeft / cellWidth);
      const endIdx = Math.ceil((scrollLeft + viewWidth) / cellWidth);

      setVisibleRange({ startIdx, endIdx });
    };

    updateRange(); // run initially
    main.addEventListener('scroll', updateRange);
    return () => main.removeEventListener('scroll', updateRange);
  }, []);

  const getRowContext = (cellKey) => {
    let rowKey;
    let coreKey = cellKey;

    if (cellKey.startsWith('roomsToSell_')) {
        rowKey = 'roomsToSell';
        coreKey = cellKey.substring('roomsToSell_'.length);
    } else if (cellKey.endsWith('_roomonly')) {
        rowKey = 'roomOnly';
        coreKey = cellKey.substring(0, cellKey.length - '_roomonly'.length);
    } else {
        rowKey = 'price';
    }

    if (coreKey.length > 10 && coreKey.lastIndexOf('_') === coreKey.length - 11) {
        const roomType = coreKey.substring(0, coreKey.length - 11);
        return { roomType, rowKey };
    }
    return {};
  };

  return (
    <div
      ref={calendarContainerRef}
      className="calendar-view"
      onClick={() => setShowDateRangePicker(false)}
    >
      
      <div className="calendar-controls">
        <div style={{ position: 'relative', zIndex: 1, display: 'inline-block', marginRight: '1rem' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDateRangePicker(prev => !prev);
            }}
            style={{
              padding: '10px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              background: '#fff',
              cursor: 'pointer'
            }}
          >
            {dateRange[0].startDate.toLocaleDateString()} - {dateRange[0].endDate.toLocaleDateString()}
          </button>
          {showDateRangePicker && (
            <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} />
          )}
        </div>
        {/*}
        <select className="calendar-select" value={month} onChange={e => setMonth(parseInt(e.target.value))}>
          {[
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
          ].map((m, idx) => (
            <option key={idx} value={idx}>{m}</option>
          ))}
        </select>
        <select className="calendar-select" value={year} onChange={e => setYear(parseInt(e.target.value))}>
          {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i).map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        */}
        <label className="promo-toggle-checkbox">
          <input
            type="checkbox"
            checked={showPromoRates}
            onChange={(e) => setShowPromoRates(e.target.checked)}
          />
          Show Promotion Rates
        </label>
        
      </div>
      

      <div className="zoom-control">
        <label htmlFor="zoom" style={{ marginRight: '10px' }}>Zoom:</label>
        <select id="zoom" value={zoom} onChange={handleZoomChange}>
          <option value={0.6}>60%</option>
          <option value={0.75}>75%</option>
          <option value={1}>100%</option>
          <option value={1.25}>125%</option>
          <option value={1.5}>150%</option>
        </select>
      </div>

      <div className="calendar-legend" style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem', fontSize: '14px', alignItems: 'center', justifyContent: 'flex-end' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <div style={{ width: '16px', height: '16px', backgroundColor: 'rgba(255, 230, 0, 0.3)', borderRadius: '2px', border: '1px solid #ccc' }}></div>
          <span>Overbooked</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <div style={{ width: '16px', height: '16px', backgroundColor: '#ffe5e5', borderRadius: '2px', border: '1px solid #ccc' }}></div>
          <span>Sold Out</span>
        </div>
      </div>
      
      <div className="rate-limit-inputs" style={{ marginBottom: '1rem', display: 'flex', gap: '1rem' }}>
        <label>
          Min Rate:
          <input
            type="number"
            value={minRate}
            onChange={e => setMinRate(parseFloat(e.target.value))}
            style={{ marginLeft: '0.5rem', width: '80px' }}
          />
        </label>
        <label>
          Max Rate:
          <input
            type="number"
            value={maxRate}
            onChange={e => setMaxRate(parseFloat(e.target.value))}
            style={{ marginLeft: '0.5rem', width: '80px' }}
          />
        </label>
      </div>
      
      <div className="calendar-scroll-container">
        <div className="calendar-scroll-wrapper" ref={mainScrollRef}>
          <div className="calendar-zoom-wrapper" style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
          <>
            {/* Weekday (Mo, Tu, We...) Row */}
            <div className="calendar-weekday-row calendar-row calendar-sticky-header">
              <div className="calendar-room header">{year}</div>
              {visibleDays.map(({ day, month, year, dateStr }) => {
                const date = new Date(year, month, day);
                const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
                return <div key={`weekday-${dateStr}`} className="calendar-day header">{weekday}</div>;
              })}
            </div>

            {/* Day Number Row (Month as left cell) */}
            <div className="calendar-daynumber-row calendar-row calendar-sticky-header">
              <div className="calendar-room header">
                {new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </div>
              {visibleDays.map(({ day, month, year, dateStr }) => (
                <div key={`daynum-${dateStr}`} className="calendar-day header">{day}</div>
              ))}
            </div>

          </>

          {/* Data Rows */}
          {loadingRates ? (
            <div className="calendar-loading-placeholder">
              <div style={{ padding: '0 16px' }}>
                <Skeleton variant="rectangular" height={30} animation="wave" sx={{ borderRadius: '6px', mb: 1 }} />
                <Skeleton variant="rectangular" height={30} animation="wave" sx={{ borderRadius: '6px', mb: 1 }} />
                <Skeleton variant="rectangular" height={30} animation="wave" sx={{ borderRadius: '6px', mb: 1 }} />
                <Skeleton variant="rectangular" height={30} animation="wave" sx={{ borderRadius: '6px', mb: 1 }} />

              </div>
            </div>
          ) : (
            Object.entries(groupedByRoom).map(([roomType, dailyRates]) => (
              <div className="scrollable-room-block" key={roomType}>
                <React.Fragment>
              <div className="calendar-row">
        <div className="calendar-room room-type-label">
         {roomType.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
          </div>
          {visibleDays.map(({ day, month: dMonth, year: dYear, dateStr }) => {
            const key = `${roomType}_${dateStr}`;
            const baseRate = dailyRates[dateStr] || {};
            const editedRate = editing[key] || {};
            const current = {
              ...baseRate,
              ...editedRate,
              availability: editedRate.hasOwnProperty('availability') ? editedRate.availability : baseRate.availability ?? true,
              breakfastGuests: editedRate.breakfastGuests ?? baseRate.breakfastGuests ?? editing[`__${roomType}_globalBreakfast`] ?? 0
            };
            return (
              <div key={`roomTypeStatus_${key}`} className="calendar-cell">
                <div
                  onClick={async () => {
                    const rateId = dailyRates[dateStr]?._id;
                    if (!rateId) {
                      // No rate ID found to toggle availability.
                      return;
                    }
                    setTogglingAvailability(prev => ({ ...prev, [key]: true }));
                    try {
                      const res = await fetch(`${apiURL}/api/rates/${rateId}/toggle-availability`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ propertyId })
                      });
                      if (!res.ok) {
                        const errorText = await res.text();
                        console.error('❌ Failed to toggle availability:', errorText);
                        setTogglingAvailability(prev => {
                          const { [key]: removed, ...rest } = prev;
                          return rest;
                        });
                        return;
                      } else {
                        const updatedRate = await res.json();
                        setRates(prev => {
                          const updated = prev.map(r => r._id === updatedRate._id ? updatedRate : r);
                          return updated;
                        });
                        const updatedDateStr = updatedRate.date;
                        const updatedRoomType = updatedRate.roomType;
                        const updatedKey = `${updatedRoomType}_${updatedDateStr}`;
                        setEditing(prev => {
                          const { [updatedKey]: removed, ...rest } = prev;
                          return rest;
                        });
                        setTogglingAvailability(prev => {
                          const { [key]: removed, ...rest } = prev;
                          return rest;
                        });
                      }
                    } catch (err) {
                      console.error('❌ Error toggling availability:', err);
                      setTogglingAvailability(prev => {
                        const { [key]: removed, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  className={`room-status-toggle ${current.availability === false ? 'unavailable' : 'available'} ${togglingAvailability[key] ? 'fading' : ''}`}
                >
                  {current.availability === false ? 'Sold Out' : 'Bookable'}
                </div>
              </div>
            );
          })}
        </div>
        {/* Custom fixed-position scrollbar */}
        <div className="custom-scrollbar-wrapper">
          <div className="custom-scrollbar-track">
            <div className="custom-scrollbar-thumb" />
          </div>
        </div>
              <div className="calendar-row">
                <div className="calendar-room">Rooms to sell</div>
                {(() => {
                  const globalRoomsToSell = editing[`__${roomType}_globalRoomsToSell`] ??
                    Object.values(dailyRates).find(r => r.roomsToSell !== undefined)?.roomsToSell ?? '';

                  return visibleDays.map(({ day, month: dMonth, year: dYear, dateStr }) => {
                    const key = `${roomType}_${dateStr}`;
                    const value = editing[key]?.roomsToSell ?? dailyRates[dateStr]?.roomsToSell ?? globalRoomsToSell ?? '';
                    return (
                      <div
                        key={`roomsToSell_${key}`}
                        className={`calendar-cell ${selectedCells.includes(`roomsToSell_${key}`) ? 'selected' : ''} ${value === 0 ? 'sold-out-column' : ''} ${dailyRates[dateStr]?.availability === false ? 'sold-out-column' : ''} ${overbookedRooms.has(key) ? 'overbooked' : ''}`}
                        onMouseDown={() => {
                          const cellKey = `roomsToSell_${key}`;
                          setIsDragging(true);
                          setSelectionContext(getRowContext(cellKey));
                          setSelectedCells([cellKey]);
                        }}
                        onMouseEnter={() => {
                          if (isDragging) {
                            const cellKey = `roomsToSell_${key}`;
                            const context = getRowContext(cellKey);
                            if (selectionContext && context.roomType === selectionContext.roomType && context.rowKey === selectionContext.rowKey) {
                              if (!selectedCells.includes(cellKey)) {
                                setSelectedCells(prev => [...prev, cellKey]);
                              }
                            }
                          }
                        }}
                        onMouseUp={async () => {
                          setIsDragging(false);
                          setSelectionContext(null);
                          if (selectedCells.length > 0) {
                            let capturedValue = null;
                            await Swal.fire({
                              input: 'number',
                              inputPlaceholder: 'Enter number of rooms',
                              showConfirmButton: false,
                              showCancelButton: false,
                              padding: '0.5rem',
                              width: '240px',
                              backdrop: true,
                              allowOutsideClick: () => {
                                capturedValue = undefined;
                                return true;
                              },
                              allowEscapeKey: true,
                              inputAttributes: {
                                autofocus: true
                              },
                              customClass: {
                                popup: 'compact-swal-popup',
                                input: 'compact-swal-input'
                              },
                              didOpen: () => {
                                const input = Swal.getInput();
                                if (input) {
                                  input.focus();
                                  input.addEventListener('keydown', (e) => {
                                    if (e.key === 'Enter' || e.key === 'Tab') {
                                      capturedValue = input.value;
                                      Swal.clickConfirm();
                                    }
                                  });
                                }
                              },
                              preConfirm: () => capturedValue
                            }).then(result => {
                              const inputValue = result.value;
                              if (inputValue !== undefined && inputValue !== null && !isNaN(inputValue)) {
                                const selected = [...selectedCells];

                                selected.forEach(selKey => {
                                  const [roomType, dateStr] = selKey.replace('roomsToSell_', '').split('_');
                                  const editKey = `${roomType}_${dateStr}`;
                                  const currentPrice = editing[editKey]?.price ?? groupedByRoom[roomType]?.[dateStr]?.price ?? 0;
                                  const currentAvailability = editing[editKey]?.availability ?? groupedByRoom[roomType]?.[dateStr]?.availability ?? true;

                                  setEditing(prev => ({
                                    ...prev,
                                    [editKey]: {
                                      ...(prev[editKey] || {}),
                                      roomsToSell: parseInt(inputValue)
                                    }
                                  }));

                                  handleBlur(roomType, dateStr, {
                                    roomsToSell: parseInt(inputValue),
                                    price: currentPrice,
                                    availability: currentAvailability
                                  });
                                });
                              }
                              setSelectedCells([]);
                            });
                          }
                        }}
                      >
                        <input
                          type="number"
                          placeholder="0"
                          value={editing[key]?.roomsToSell ?? dailyRates[dateStr]?.roomsToSell ?? ''}
                          onChange={e => {
                            const newValue = e.target.value;
                            setEditing(prev => ({
                              ...prev,
                              [key]: {
                                ...(prev[key] || {}),
                                roomsToSell: newValue
                              }
                            }));
                          }}
                          onBlur={(e) => {
                            setTimeout(() => handleBlur(roomType, dateStr), 0);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === 'Tab') {
                              e.target.blur();
                            }
                          }}
                          className="inline-input"
                          style={{
                            textAlign: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            lineHeight: 'normal',
                            padding: '0',
                            margin: '0 auto',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    );
                  });
                })()}
              </div>

              <div className="calendar-row">
                <div className="calendar-room">Net booked</div>
                {visibleDays.map(({ day, month: dMonth, year: dYear, dateStr }) => {
                  const key = `${roomType}_${dateStr}`;
                  const normalizedRoomType = roomType.trim().toLowerCase();
                  const value = netBookedSummary[normalizedRoomType]?.[dateStr];
                  // Highlight sold-out if roomsToSell is 0
                  const roomsToSellValue = editing[`${roomType}_${dateStr}`]?.roomsToSell ?? dailyRates[dateStr]?.roomsToSell;
                  return (
                    <div
                      key={`netBooked_${roomType}_${dateStr}`}
                      className={`calendar-cell ${(roomsToSellValue === 0 ? 'sold-out-column' : '')} ${dailyRates[dateStr]?.availability === false ? 'sold-out-column' : ''} ${overbookedRooms.has(key) ? 'overbooked' : ''}`}>
                      {value ?? 0}
                    </div>
                  );
                })}
              </div>
              <div className="calendar-row">
                <div className="calendar-room">
                  <div className="breakfast-section">
                    <div className="label">
                      <svg className="bk-icon -streamline-food_coffee" height="16" width="16" viewBox="0 0 24 24" role="presentation" aria-hidden="true" focusable="false" style={{ marginRight: '6px' }}>
                        <path d="M3.75 4.5h12a.75.75 0 0 1 .75.75v7.5a6.75 6.75 0 0 1-13.5 0v-7.5a.75.75 0 0 1 .75-.75zm0-1.5A2.25 2.25 0 0 0 1.5 5.25v7.5a8.25 8.25 0 0 0 16.5 0v-7.5A2.25 2.25 0 0 0 15.75 3h-12zm-3 18h22.5a.75.75 0 0 0 0-1.5H.75a.75.75 0 0 0 0 1.5zm16.5-15h1.5a3.763 3.763 0 0 1 3.75 3.752 3.762 3.762 0 0 1-3.752 3.748H17.1a.75.75 0 0 0 0 1.5h1.65A5.263 5.263 0 0 0 24 9.752 5.264 5.264 0 0 0 18.752 4.5H17.25a.75.75 0 0 0 0 1.5z"></path>
                      </svg>
                      <span style={{ color: '#000' }}>Breakfast Included</span>
                    </div>
                    <div className="label">
                      x {editing[`__${roomType}_globalBreakfast`] ?? Object.values(dailyRates).find(r => r.breakfastGuests !== undefined)?.breakfastGuests ?? 0}
                    </div>
                  </div>
                </div>
                {visibleDays.map(({ day, month: dMonth, year: dYear, dateStr }) => {
                  const key = `${roomType}_${dateStr}`;
                  const current = editing[key] || dailyRates[dateStr] || {};
                  const roomsToSellValue = editing[key]?.roomsToSell ?? dailyRates[dateStr]?.roomsToSell;
                  return (
                    <div
                      key={key}
                      className={`calendar-cell ${selectedCells.includes(key) ? 'selected' : ''} ${(roomsToSellValue === 0 ? 'sold-out-column' : '')} ${dailyRates[dateStr]?.availability === false ? 'sold-out-column' : ''} ${overbookedRooms.has(key) ? 'overbooked' : ''}`}
                      onMouseDown={() => {
                        const cellKey = key;
                        setIsDragging(true);
                        setSelectionContext(getRowContext(cellKey));
                        setSelectedCells([cellKey]);
                      }}
                      onMouseEnter={() => {
                        if (isDragging) {
                          const cellKey = key;
                          const context = getRowContext(cellKey);
                          if (selectionContext && context.roomType === selectionContext.roomType && context.rowKey === selectionContext.rowKey) {
                            if (!selectedCells.includes(cellKey)) {
                              setSelectedCells(prev => [...prev, cellKey]);
                            }
                          }
                        }
                      }}
                      onMouseUp={async () => {
                        setIsDragging(false);
                        setSelectionContext(null);
                        if (selectedCells.length > 0) {
                      let capturedValue = null;
                      await Swal.fire({
                            input: 'number',
                            inputPlaceholder: 'Enter rate',
                            showConfirmButton: false,
                            showCancelButton: false,
                            padding: '0.5rem',
                            width: '240px',
            backdrop: true,
            allowOutsideClick: () => {
              capturedValue = undefined;
              return true;
            },
                            allowEscapeKey: true,
                            inputAttributes: {
                              autofocus: true
                            },
                            customClass: {
                              popup: 'compact-swal-popup',
                              input: 'compact-swal-input'
                            },
                            didOpen: () => {
                              const input = Swal.getInput();
                              if (input) {
                                input.focus();
                                input.addEventListener('keydown', (e) => {
                                  if (e.key === 'Enter' || e.key === 'Tab') {
                                    capturedValue = input.value;
                                    Swal.clickConfirm();
                                  }
                                });
                              }
                            },
                            preConfirm: () => capturedValue
                          }).then(result => {
                            const inputValue = result.value;
                            if (inputValue !== undefined && inputValue !== null && !isNaN(inputValue)) {
                              const selected = [...selectedCells];
                              selected.forEach(selKey => {
                                if (!selKey.includes('_')) return;
                                const [roomType, ...rest] = selKey.split('_');
                                const dateStr = rest.join('_');
                                const editKey = `${roomType}_${dateStr}`;
                              setEditing(prev => ({
                                ...prev,
                                [editKey]: {
                                  ...(prev[editKey] || {}),
                                  price: parseFloat(inputValue)
                                }
                              }));
                              handleBlur(roomType, dateStr, { price: parseFloat(inputValue) });
                              });
                            }
                            setSelectedCells([]);
                          });
                        }
                      }}
                    >
                      <input
                        type="text"
                        placeholder="$"
                        value={editing[key]?.price ?? dailyRates[dateStr]?.price ?? ''}
                        className={`inline-input ${
                          (editing[key]?.price ?? dailyRates[dateStr]?.price ?? 0) < minRate ||
                          (editing[key]?.price ?? dailyRates[dateStr]?.price ?? 0) > maxRate
                            ? 'rate-out-of-bounds'
                            : ''
                        }`}
                        onChange={e => {
                          const newValue = parseFloat(e.target.value);
                          setEditing(prev => ({
                            ...prev,
                            [key]: {
                              ...(prev[key] || {}),
                              price: isNaN(newValue) ? 0 : newValue
                            }
                          }));
                        }}
                      onBlur={() => {
                        handleBlur(roomType, dateStr);
                      }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === 'Tab') {
                            e.target.blur();
                          }
                        }}
                      
                      />
                    </div>
                  );
                })}
              </div>

              <div className="calendar-row">
              <div className="calendar-room">Room Only</div>
              {visibleDays.map(({ day, month: dMonth, year: dYear, dateStr }) => {
                const key = `${roomType}_${dateStr}`;
                const currentRoomOnlyRate = editing[key]?.roomOnlyRate ?? dailyRates[dateStr]?.roomOnlyRate ?? '';
                const roomsToSellValue = editing[key]?.roomsToSell ?? dailyRates[dateStr]?.roomsToSell;
                return (
                  <div
                    key={`roomonly_${key}`}
                    className={`calendar-cell ${selectedCells.includes(key + '_roomonly') ? 'selected' : ''} ${currentRoomOnlyRate !== '' ? 'highlighted' : ''} ${(roomsToSellValue === 0 ? 'sold-out-column' : '')} ${dailyRates[dateStr]?.availability === false ? 'sold-out-column' : ''} ${overbookedRooms.has(key) ? 'overbooked' : ''}`}
                    onMouseDown={() => {
                      const cellKey = key + '_roomonly';
                      setIsDragging(true);
                      setSelectionContext(getRowContext(cellKey));
                      setSelectedCells([cellKey]);
                    }}
                    onMouseEnter={() => {
                      if (isDragging) {
                        const cellKey = key + '_roomonly';
                        const context = getRowContext(cellKey);
                        if (selectionContext && context.roomType === selectionContext.roomType && context.rowKey === selectionContext.rowKey) {
                          if (!selectedCells.includes(cellKey)) {
                            setSelectedCells(prev => [...prev, cellKey]);
                          }
                        }
                      }
                    }}
                    onMouseUp={async () => {
                      setIsDragging(false);
                      setSelectionContext(null);
                      if (selectedCells.length > 0) {
                        let capturedValue = null;
                        await Swal.fire({
                          input: 'number',
                          inputPlaceholder: 'Enter room-only rate',
                          showConfirmButton: false,
                          showCancelButton: false,
                          padding: '0.5rem',
                          width: '240px',
                          backdrop: true,
                          allowOutsideClick: () => {
                            capturedValue = undefined;
                            return true;
                          },
                          allowEscapeKey: true,
                          inputAttributes: {
                            autofocus: true
                          },
                          customClass: {
                            popup: 'compact-swal-popup',
                            input: 'compact-swal-input'
                          },
                          didOpen: () => {
                            const input = Swal.getInput();
                            if (input) {
                              input.focus();
                              input.addEventListener('keydown', (e) => {
                                if (e.key === 'Enter' || e.key === 'Tab') {
                                  capturedValue = input.value;
                                  Swal.clickConfirm();
                                }
                              });
                            }
                          },
                          preConfirm: () => capturedValue
                        }).then(result => {
                          const inputValue = result.value;
                          if (inputValue !== undefined && !isNaN(inputValue)) {
                            const selected = [...selectedCells];
                            selected.forEach(selKey => {
                              const originalKey = selKey.replace('_roomonly', '');
                              const [roomType, ...rest] = originalKey.split('_');
                              const dateStr = rest.join('_');
                              const editKey = `${roomType}_${dateStr}`;
                              setEditing(prev => ({
                                ...prev,
                                [editKey]: {
                                  ...(prev[editKey] || {}),
                                  roomOnlyRate: parseFloat(inputValue),
                                  breakfastGuests: 0,
                                  ratePlan: 'roomOnly'
                                }
                              }));
                              handleBlur(roomType, dateStr, {
                                roomOnlyRate: parseFloat(inputValue),
                                breakfastGuests: 0,
                                ratePlan: 'roomOnly'
                              });
                            });
                          }
                          setSelectedCells([]);
                        });
                      }
                    }}
                  >
                    <input
                      type="text"
                      placeholder="$"
                      value={currentRoomOnlyRate}
                      className={`inline-input ${
                        currentRoomOnlyRate < minRate || currentRoomOnlyRate > maxRate
                          ? 'rate-out-of-bounds'
                          : ''
                      }`}
                      onChange={e => {
                        const newValue = parseFloat(e.target.value);
                        setEditing(prev => ({
                          ...prev,
                          [key]: {
                            ...(prev[key] || {}),
                            roomOnlyRate: isNaN(newValue) ? '' : newValue,
                            breakfastGuests: 0,
                            ratePlan: 'roomOnly'
                          }
                        }));
                      }}
                      onBlur={() => {
                        handleBlur(roomType, dateStr, { breakfastGuests: 0, ratePlan: 'roomOnly' });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === 'Tab') {
                          e.target.blur();
                        }
                      }}
                      
                    />
                  </div>
                );
              })}
            </div>
              {showPromoRates && (
                <>
                  <div className="calendar-row">
                    <div className="calendar-room">% Breakfast Included</div>
                    {visibleDays.map(({ day, month: dMonth, year: dYear, dateStr }) => {
                      const key = `${roomType}_${dateStr}`;
                      const base = dailyRates[dateStr];
                      const rawPrice = editing[`${roomType}_${dateStr}`]?.price;
                      const price = !isNaN(rawPrice) && rawPrice !== undefined
                        ? parseFloat(rawPrice)
                        : parseFloat(base?.price ?? NaN);
                      if (isNaN(price) || price === 0) {
                        return (
                          <div
                            key={`promoBreakfast_${roomType}_${dateStr}`}
                            className={`calendar-cell read-only ${dailyRates[dateStr]?.availability === false ? 'sold-out-column' : ''} ${overbookedRooms.has(key) ? 'overbooked' : ''}`}
                          >
                            0.00
                          </div>
                        );
                      }
                      const promo = promotionRate.find(p => {
                        const isWithinDate = new Date(p.startDate) <= new Date(dateStr) && new Date(p.endDate) >= new Date(dateStr);
                        const isRoomIncluded = p.roomTypes.map(r => r.toLowerCase().trim())
                          .includes(roomType.toLowerCase().trim());
                        const isSameProperty = p.propertyId === propertyId;
                        return isWithinDate && isRoomIncluded && isSameProperty;
                      });
                      const discounted = dailyRates[dateStr]?.promotionPrice ?? 0;
                      return (
                        <div
                          key={`promoBreakfast_${roomType}_${dateStr}`}
                          className={`calendar-cell read-only ${promo ? 'has-promo' : ''} ${dailyRates[dateStr]?.availability === false ? 'sold-out-column' : ''} ${overbookedRooms.has(key) ? 'overbooked' : ''}`}
                        >
                          {discounted.toFixed(2)}
                        </div>
                      );
                    })}
                  </div>

                  <div className="calendar-row">
                    <div className="calendar-room">% Room Only</div>
                    {visibleDays.map(({ day, month: dMonth, year: dYear, dateStr }) => {
                      const key = `${roomType}_${dateStr}`;
                      const promo = promotionRate.find(p => {
                        const isWithinDate = new Date(p.startDate) <= new Date(dateStr) && new Date(p.endDate) >= new Date(dateStr);
                        const isRoomIncluded = p.roomTypes.map(r => r.toLowerCase().trim())
                          .includes(roomType.toLowerCase().trim());
                        const isSameProperty = p.propertyId === propertyId;
                        return isWithinDate && isRoomIncluded && isSameProperty;
                      });
                      const discounted = dailyRates[dateStr]?.promotionRoomOnlyRate;
                      const hasPromo = !!promo;
                      return (
                        <div
                          key={`promoRoomOnly_${roomType}_${dateStr}`}
                          className={`calendar-cell read-only ${hasPromo ? 'has-promo' : ''} ${dailyRates[dateStr]?.availability === false ? 'sold-out-column' : ''} ${overbookedRooms.has(key) ? 'overbooked' : ''}`}
                        >
                          {(discounted ?? 0).toFixed(2)}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
                </React.Fragment>
              </div>
            ))
          )}
          </div>
        </div>
        <div
          className="horizontal-scrollbar-mirror"
          ref={el => {
            mirrorScrollRef.current = el;
            if (el) {
              console.log("🟩 Scrollbar container mounted:", el);
            }
          }}
        >
          <div
            style={{
              width: `${visibleDays.length * 75 + 160}px`,
              height: '20px',
              pointerEvents: 'auto',
              background: 'transparent'
            }}
            ref={el => {
              if (el) {
                console.log("🟦 Inner scrollable div width:", el.style.width);
              }
            }}
          >
            &nbsp;
          </div>
        </div>
      </div>
      <ToastContainer position="top-center" autoClose={3000} />
    </div>
  );
};

export default CalendarView;
